// IncidentNotices — the transient recovery-notice stack (Brief 5 / pt10 + pt20).
//
// The PUSH surface the spec asks for: a caught-and-handled incident is
// SURFACED honestly the instant it happens — "caught → bounded → reversed, never
// cosmetic". It sits at the top of the conversation thread as a thin transient
// stack, NEVER a modal, so it never blocks the chat. It COMPLEMENTS the
// SessionStatusPanel (the pull-view you open to catch up) — this is the proactive
// notice you don't have to go looking for.
//
// Honesty in the chrome (the honesty dial):
//   - `recovered`  — CALM. The neutral/calm tones (signal-calm / signal-stopped),
//                    no amber, no animation; the honest "nothing lost" line shows
//                    only where the descriptor says so (a FACT). It auto-dismisses
//                    (the hook), and is dismissable early.
//   - `needs_input`— the ONE legitimate "this is on you". The reserved attention
//                    amber (signal-attention) is allowed here and ONLY here; it is
//                    static (no alarm pulse) and stays until acknowledged.
//
// Token-pure + a11y: sentence-case copy, `font-code` is not needed (this is prose,
// not technical values); each row is a status with a plain dismiss control; the
// stack is an aria-live polite region so a screen reader hears the recovery
// without it stealing focus.

import type { ActiveIncident } from "../hooks/useIncidents";
import "../styles/incidentNotices.css";

export interface IncidentNoticesProps {
  /** The active notices (from `useIncidents`), oldest-first. */
  notices: ActiveIncident[];
  /** Dismiss one by id (the hook's `dismiss`). */
  onDismiss: (id: string) => void;
}

export function IncidentNotices({ notices, onDismiss }: IncidentNoticesProps) {
  if (notices.length === 0) return null;
  return (
    <div
      className="wt-incidents"
      role="status"
      aria-live="polite"
      aria-label="Recovery notices"
    >
      {notices.map((n) => {
        const needsInput = n.descriptor.severity === "needs_input";
        return (
          <div
            key={n.id}
            className="wt-incident"
            data-severity={n.descriptor.severity}
            data-kind={n.descriptor.kind}
          >
            <span className="wt-incident__dot" aria-hidden="true" />
            <div className="wt-incident__text">
              <span className="wt-incident__summary">
                {n.descriptor.summary}
              </span>
              {/* The honest no-loss reassurance — shown ONLY when it's a FACT
                  (descriptor.lost === false) AND the system handled it. We never
                  print "nothing lost" for a needs-you incident (it isn't calm) or
                  speculatively. */}
              {n.descriptor.handled && !n.descriptor.lost ? (
                <span className="wt-incident__note">Nothing was lost.</span>
              ) : null}
            </div>
            <button
              type="button"
              className="wt-incident__dismiss"
              onClick={() => onDismiss(n.id)}
              aria-label={needsInput ? "Acknowledge" : "Dismiss"}
            >
              {needsInput ? "Got it" : "Dismiss"}
            </button>
          </div>
        );
      })}
    </div>
  );
}
